import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CalendarDays, CircleDollarSign, RefreshCw, ShieldCheck, Wrench } from 'lucide-react';
import { obtenerDashboardKpis, obtenerMetricasFinancieras } from '../servicios/api';

const DashboardPage = () => {
  const [kpis, setKpis] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [metricasFinancieras, setMetricasFinancieras] = useState(null);
  const [cargandoFinanzas, setCargandoFinanzas] = useState(true);
  const [errorFinanzas, setErrorFinanzas] = useState('');
  const [periodo, setPeriodo] = useState('mes_actual');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const respuesta = await obtenerDashboardKpis();
      if (!respuesta.exito) throw new Error(respuesta.mensaje || 'No se pudieron cargar las métricas.');
      setKpis(respuesta.kpis);
    } catch (loadError) {
      setError(loadError.response?.data?.mensaje || loadError.message || 'No se pudieron cargar las métricas.');
    } finally {
      setCargando(false);
    }
  }, []);

  const cargarFinanzas = useCallback(async () => {
    if (periodo === 'personalizado' && (!fechaInicio || !fechaFin)) return;
    setCargandoFinanzas(true);
    setErrorFinanzas('');
    try {
      const filtros = { periodo };
      const tecnicoId = localStorage.getItem('filtro_tecnico_id');
      if (tecnicoId) filtros.tecnico_id = tecnicoId;
      if (periodo === 'personalizado') {
        filtros.fecha_inicio = fechaInicio;
        filtros.fecha_fin = fechaFin;
      }
      const respuesta = await obtenerMetricasFinancieras(filtros);
      if (!respuesta.exito) throw new Error(respuesta.mensaje || 'No se pudieron cargar las métricas financieras.');
      setMetricasFinancieras(respuesta.metricas);
    } catch (loadError) {
      setErrorFinanzas(loadError.response?.data?.mensaje || loadError.message || 'No se pudieron cargar las métricas financieras.');
    } finally {
      setCargandoFinanzas(false);
    }
  }, [periodo, fechaInicio, fechaFin]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    cargarFinanzas();
  }, [cargarFinanzas]);

  useEffect(() => {
    const actualizarPorTecnico = () => {
      cargar();
      cargarFinanzas();
    };
    window.addEventListener('filtro-tecnico-cambiado', actualizarPorTecnico);
    return () => window.removeEventListener('filtro-tecnico-cambiado', actualizarPorTecnico);
  }, [cargar, cargarFinanzas]);

  const tarjetas = [
    { label: 'Avisos pendientes esta semana', value: kpis?.avisos_pendientes_semana ?? '—', icon: CalendarDays, color: 'text-orange-300' },
    { label: 'Avisos pendientes este mes', value: kpis?.avisos_pendientes_mes ?? '—', icon: AlertCircle, color: 'text-cyan-300' },
    { label: 'Garantías del mes', value: kpis?.garantias_mes ?? '—', icon: ShieldCheck, color: 'text-amber-300' },
    { label: 'Reparaciones pagadas', value: kpis?.reparaciones_pagadas_mes ?? '—', icon: Wrench, color: 'text-emerald-300' },
  ];
  const formatoEuros = (value) => `${Number(value || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const metricasTarjetas = [
    { label: 'Ingresos brutos', value: formatoEuros(metricasFinancieras?.ingresos_brutos), icon: CircleDollarSign, color: 'text-green-300' },
    { label: 'Gastos en repuestos', value: formatoEuros(metricasFinancieras?.gastos_repuestos), icon: Wrench, color: 'text-amber-300' },
    { label: 'Beneficio neto', value: formatoEuros(metricasFinancieras?.beneficio_neto), icon: CircleDollarSign, color: 'text-emerald-300' },
    { label: 'Margen medio', value: `${Number(metricasFinancieras?.margen_beneficio_medio || 0).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`, icon: ShieldCheck, color: 'text-cyan-300' },
    { label: 'Avisos completados', value: metricasFinancieras?.avisos_completados ?? '—', icon: CalendarDays, color: 'text-orange-300' },
  ];
  const ratioGarantias = Number(kpis?.porcentaje_garantias_vs_reparaciones || 0);
  const periodoLabel = {
    hoy: 'hoy',
    mes_actual: 'este mes',
    mes_anterior: 'el mes anterior',
    ano_actual: 'este año',
    personalizado: 'el rango seleccionado',
  }[periodo];

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-7 flex flex-wrap items-end justify-between gap-4 border-b border-slate-800 pb-5">
          <div>
            <p className="text-xs font-semibold uppercase text-orange-300">Taller · Control operativo</p>
            <h1 className="mt-1 text-3xl font-bold">Panel de control</h1>
            <p className="mt-1 text-sm text-slate-400">Resumen de actividad e ingresos según avisos completados</p>
          </div>
          <button type="button" onClick={() => { cargar(); cargarFinanzas(); }} disabled={cargando || cargandoFinanzas} className="inline-flex items-center gap-2 rounded border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-900 disabled:opacity-50">
            <RefreshCw size={16} className={cargando ? 'animate-spin' : ''} /> Actualizar
          </button>
        </header>

        {error && <div role="alert" className="mb-5 rounded border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-200">{error}</div>}

        <section aria-label="Resumen financiero histórico" className="mb-8">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold">Resumen financiero</h2>
              <p className="mt-1 text-sm text-slate-400">Ingresos por reparación y venta de repuestos frente al coste de compra.</p>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm text-slate-300">
                Periodo
                <select value={periodo} onChange={(event) => setPeriodo(event.target.value)} className="mt-1 block rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100">
                  <option value="hoy">Hoy</option>
                  <option value="mes_actual">Mes actual</option>
                  <option value="mes_anterior">Mes anterior</option>
                  <option value="ano_actual">Año actual</option>
                  <option value="personalizado">Rango personalizado</option>
                </select>
              </label>
              {periodo === 'personalizado' && (
                <>
                  <label className="text-sm text-slate-300">
                    Desde
                    <input type="date" value={fechaInicio} onChange={(event) => setFechaInicio(event.target.value)} className="mt-1 block rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100" />
                  </label>
                  <label className="text-sm text-slate-300">
                    Hasta
                    <input type="date" value={fechaFin} min={fechaInicio || undefined} onChange={(event) => setFechaFin(event.target.value)} className="mt-1 block rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100" />
                  </label>
                </>
              )}
            </div>
          </div>
          <p className="mb-3 text-xs text-slate-500">Periodo: {periodoLabel}</p>
          {errorFinanzas && <div role="alert" className="mb-4 rounded border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-200">{errorFinanzas}</div>}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {metricasTarjetas.map(({ label, value, icon: Icon, color }) => (
              <article key={label} className="rounded-md border border-slate-800 bg-slate-900 p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-slate-400">{label}</p>
                  <Icon size={18} className={color} />
                </div>
                <p className={`mt-4 text-2xl font-semibold ${color}`}>{cargandoFinanzas ? '…' : value}</p>
              </article>
            ))}
          </div>
          {(Number(metricasFinancieras?.repuestos_sin_coste || 0) > 0 || Number(metricasFinancieras?.materiales_sin_clasificar || 0) > 0) && (
            <p role="status" className="mt-3 rounded border border-amber-800 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">
              El margen de {periodoLabel} es parcial: hay {Number(metricasFinancieras?.repuestos_sin_coste || 0)} repuesto(s) sin coste y {Number(metricasFinancieras?.materiales_sin_clasificar || 0)} línea(s) histórica(s) sin clasificar.
            </p>
          )}
        </section>

        <section aria-label="Indicadores operativos" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {tarjetas.map(({ label, value, icon: Icon, color }) => (
            <article key={label} className="rounded-md border border-slate-800 bg-slate-900 p-4">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-slate-400">{label}</p>
                <Icon size={18} className={color} />
              </div>
              <p className={`mt-4 text-2xl font-semibold ${color}`}>{cargando ? '…' : value}</p>
            </article>
          ))}
        </section>

        <section className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.8fr)]">
          <article className="rounded-md border border-slate-800 bg-slate-900 p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold">Garantías frente a reparaciones pagadas</h2>
                <p className="mt-1 text-sm text-slate-400">Proporción de garantías sobre ambos tipos de servicio este mes</p>
              </div>
              <span className="text-xl font-semibold text-amber-300">{cargando ? '—' : `${ratioGarantias.toLocaleString('es-ES')}%`}</span>
            </div>
            <div className="mt-5 h-3 overflow-hidden rounded bg-slate-800" role="progressbar" aria-valuenow={ratioGarantias} aria-valuemin="0" aria-valuemax="100" aria-label="Porcentaje de garantías">
              <div className="h-full rounded bg-amber-400 transition-[width]" style={{ width: `${Math.min(100, Math.max(0, ratioGarantias))}%` }} />
            </div>
            <div className="mt-3 flex justify-between text-xs text-slate-500"><span>0%</span><span>100%</span></div>
          </article>

          <article className="rounded-md border border-slate-800 bg-slate-900 p-5">
            <h2 className="text-base font-semibold">Carga pendiente semanal y mensual</h2>
            <div className="mt-5 space-y-4">
              {[['Semana', kpis?.avisos_pendientes_semana || 0], ['Mes', kpis?.avisos_pendientes_mes || 0]].map(([label, value]) => {
                const max = Math.max(Number(kpis?.avisos_pendientes_semana || 0), Number(kpis?.avisos_pendientes_mes || 0), 1);
                return (
                  <div key={label}>
                    <div className="mb-1 flex justify-between text-sm"><span className="text-slate-400">{label}</span><span className="font-medium">{cargando ? '—' : value}</span></div>
                    <div className="h-2 rounded bg-slate-800"><div className="h-2 rounded bg-cyan-400" style={{ width: `${(Number(value) / max) * 100}%` }} /></div>
                  </div>
                );
              })}
            </div>
          </article>
        </section>
      </div>
    </main>
  );
};

export default DashboardPage;
